import { useState, useEffect } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

type DeviceType = 'ios' | 'android' | 'huawei' | 'xiaomi' | 'oppo' | 'vivo' | 'other-android' | 'desktop' | 'unknown';

function detectDevice(): DeviceType {
  const ua = navigator.userAgent.toLowerCase();
  if (/iphone|ipad|ipod/.test(ua)) return 'ios';
  if (/huawei|honor/.test(ua)) return 'huawei';
  if (/xiaomi|miui|redmi/.test(ua)) return 'xiaomi';
  if (/oppo|realme/.test(ua)) return 'oppo';
  if (/vivo/.test(ua)) return 'vivo';
  if (/android/.test(ua)) return 'android';
  if (/windows|macintosh|linux/.test(ua) && !/android/.test(ua)) return 'desktop';
  return 'unknown';
}

function isInStandaloneMode(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    ('standalone' in window.navigator && (window.navigator as { standalone?: boolean }).standalone === true)
  );
}

const DISMISSED_KEY = 'pwa_install_dismissed';
const DISMISSED_EXPIRY_DAYS = 7;

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);
  const [device, setDevice] = useState<DeviceType>('unknown');
  const [installing, setInstalling] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    // Already installed as PWA
    if (isInStandaloneMode()) return;

    // Check if user dismissed recently
    const dismissedAt = localStorage.getItem(DISMISSED_KEY);
    if (dismissedAt) {
      const diff = Date.now() - parseInt(dismissedAt, 10);
      if (diff < DISMISSED_EXPIRY_DAYS * 24 * 60 * 60 * 1000) return;
    }

    const detectedDevice = detectDevice();
    setDevice(detectedDevice);

    // Listen for Chrome/Android install prompt
    const handleBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      // Show after 3 seconds
      setTimeout(() => setVisible(true), 3000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // For iOS and Chinese phones without beforeinstallprompt, show manual guide after delay
    if (['ios', 'huawei', 'xiaomi', 'oppo', 'vivo'].includes(detectedDevice)) {
      setTimeout(() => setVisible(true), 4000);
    }

    // Listen for successful install
    window.addEventListener('appinstalled', () => {
      setInstalled(true);
      setVisible(false);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      setInstalling(true);
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      setInstalling(false);
      if (outcome === 'accepted') {
        setInstalled(true);
        setVisible(false);
      } else {
        handleDismiss();
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setVisible(false);
    localStorage.setItem(DISMISSED_KEY, Date.now().toString());
  };

  if (!visible || installed) return null;

  const isIOS = device === 'ios';
  const isChinesePhone = ['huawei', 'xiaomi', 'oppo', 'vivo'].includes(device);
  const hasNativePrompt = !!deferredPrompt;

  // Device label
  const deviceLabels: Record<string, string> = {
    huawei: '华为手机',
    xiaomi: '小米手机',
    oppo: 'OPPO手机',
    vivo: 'vivo手机',
    ios: 'iPhone / iPad',
    android: 'Android手机',
    desktop: '电脑浏览器',
  };
  const deviceLabel = deviceLabels[device] || '您的设备';

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9999] flex justify-center px-4 pb-4 md:pb-6 pointer-events-none">
      <div
        className="pointer-events-auto w-full max-w-sm bg-white rounded-2xl overflow-hidden"
        style={{ boxShadow: '0 8px 40px rgba(0,0,0,0.18)' }}
      >
        {/* Top accent bar */}
        <div className="h-1 bg-gradient-to-r from-red-500 via-amber-400 to-red-600" />

        <div className="p-5">
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-2xl overflow-hidden flex-shrink-0 bg-red-50 flex items-center justify-center">
                <img
                  src="https://static.readdy.ai/image/3fc89005e5f8f541d0781d07714068dd/b31ed3570265bb91107b593ba40da4ad.jpeg"
                  alt="金榜旺财"
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <p className="font-bold text-gray-900 text-base leading-tight">安装金榜旺财</p>
                <p className="text-xs text-gray-500 mt-0.5">添加到桌面，随时快速访问</p>
                <div className="flex items-center gap-1 mt-1">
                  <span className="text-xs text-amber-500 font-medium">{deviceLabel}</span>
                  <span className="text-gray-300 text-xs">·</span>
                  <span className="text-xs text-green-600 font-medium">免费安装</span>
                </div>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="w-7 h-7 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all cursor-pointer flex-shrink-0 mt-0.5"
            >
              <i className="ri-close-line text-base"></i>
            </button>
          </div>

          {/* Benefits */}
          <div className="grid grid-cols-3 gap-2 mb-4">
            {[
              { icon: 'ri-flashlight-line', label: '快速启动', color: 'text-amber-500' },
              { icon: 'ri-wifi-off-line', label: '离线可用', color: 'text-green-500' },
              { icon: 'ri-notification-3-line', label: '消息通知', color: 'text-red-500' },
            ].map((item) => (
              <div key={item.label} className="flex flex-col items-center gap-1 bg-gray-50 rounded-xl py-2.5 px-1">
                <div className={`w-6 h-6 flex items-center justify-center ${item.color}`}>
                  <i className={`${item.icon} text-lg`}></i>
                </div>
                <span className="text-xs text-gray-600 font-medium">{item.label}</span>
              </div>
            ))}
          </div>

          {/* Action area */}
          {hasNativePrompt ? (
            // Chrome / Android native install
            <button
              onClick={handleInstall}
              disabled={installing}
              className="w-full py-3 rounded-xl bg-red-600 text-white font-bold text-sm hover:bg-red-700 active:scale-95 transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
            >
              {installing ? (
                <>
                  <i className="ri-loader-4-line animate-spin text-base"></i>
                  安装中...
                </>
              ) : (
                <>
                  <i className="ri-download-line text-base"></i>
                  立即安装到桌面
                </>
              )}
            </button>
          ) : isIOS ? (
            // iOS manual guide
            <div className="bg-amber-50 rounded-xl p-3">
              <p className="text-xs font-bold text-amber-800 mb-2 flex items-center gap-1">
                <i className="ri-information-line"></i>
                iPhone 安装步骤
              </p>
              <div className="space-y-1.5">
                {[
                  { step: '1', text: '点击底部工具栏的', icon: 'ri-share-box-line', iconLabel: '分享按钮' },
                  { step: '2', text: '向下滑动，选择', icon: 'ri-add-box-line', iconLabel: '添加到主屏幕' },
                  { step: '3', text: '点击右上角"添加"即完成' },
                ].map((item) => (
                  <div key={item.step} className="flex items-center gap-2 text-xs text-amber-700">
                    <span className="w-4 h-4 rounded-full bg-amber-400 text-white flex items-center justify-center font-bold text-xs flex-shrink-0">
                      {item.step}
                    </span>
                    <span>{item.text}</span>
                    {item.icon && (
                      <span className="flex items-center gap-0.5 font-medium">
                        <i className={`${item.icon} text-sm`}></i>
                        {item.iconLabel}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : isChinesePhone ? (
            // Chinese phone manual guide
            <div className="bg-red-50 rounded-xl p-3">
              <p className="text-xs font-bold text-red-800 mb-2 flex items-center gap-1">
                <i className="ri-information-line"></i>
                {deviceLabel} 安装步骤
              </p>
              <div className="space-y-1.5">
                {[
                  { step: '1', text: '点击浏览器右上角菜单（三个点）' },
                  { step: '2', text: '选择"添加到桌面"或"添加到主屏幕"' },
                  { step: '3', text: '确认添加，桌面即出现图标' },
                ].map((item) => (
                  <div key={item.step} className="flex items-center gap-2 text-xs text-red-700">
                    <span className="w-4 h-4 rounded-full bg-red-400 text-white flex items-center justify-center font-bold text-xs flex-shrink-0">
                      {item.step}
                    </span>
                    <span>{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            // Desktop / other
            <div className="bg-gray-50 rounded-xl p-3 text-center">
              <p className="text-xs text-gray-500">
                在 Chrome 浏览器地址栏右侧点击
                <i className="ri-install-line mx-1 text-gray-700"></i>
                安装图标，即可添加到桌面
              </p>
            </div>
          )}

          {/* Dismiss link */}
          <button
            onClick={handleDismiss}
            className="w-full text-center text-xs text-gray-400 hover:text-gray-600 mt-3 cursor-pointer transition-colors"
          >
            暂不安装，{DISMISSED_EXPIRY_DAYS}天后再提醒
          </button>
        </div>
      </div>
    </div>
  );
}
